import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import type { UserPreferences } from '@localagent/sdk';
import { getSDK } from '../services/sdk-instance';
import { holdsIdentityAtom } from '../stores/auth/atoms';
import { sdkConfiguredAtom } from '../stores/sdk/atoms';

const QK = ['user', 'preferences'];

export function useUserPreferences() {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);
  return useQuery<UserPreferences, Error>({
    queryKey: QK,
    queryFn: () => getSDK().user.getPreferences(),
    enabled: Boolean(isAuthenticated && sdkConfigured),
  });
}

export function useUpdatePreferences() {
  const queryClient = useQueryClient();
  return useMutation<UserPreferences, Error, Partial<UserPreferences>, { prev?: UserPreferences }>({
    mutationFn: (patch) => getSDK().user.updatePreferences(patch),
    onMutate: async (patch) => {
      const prev = queryClient.getQueryData<UserPreferences>(QK);
      queryClient.setQueryData<UserPreferences>(QK, { ...(prev ?? {}), ...patch });
      await queryClient.cancelQueries({ queryKey: QK });
      return { prev };
    },
    onError: (_e, _patch, ctx) => {
      if (ctx?.prev !== undefined) queryClient.setQueryData(QK, ctx.prev);
    },
    onSuccess: (data) => {
      queryClient.setQueryData<UserPreferences>(QK, data);
    },
    onSettled: () => { void queryClient.invalidateQueries({ queryKey: QK }); },
  });
}

export function useFilesTreeWidthPreference(): {
  filesTreeWidth: number | undefined;
  setFilesTreeWidth: (width: number) => void;
  isLoading: boolean;
} {
  const { data, isLoading } = useUserPreferences();
  const mutation = useUpdatePreferences();
  return {
    filesTreeWidth: data?.workspace_files_tree_width,
    setFilesTreeWidth: (width) => mutation.mutate({ workspace_files_tree_width: width }),
    isLoading,
  };
}

// (app-shell ADR-c857a) — the chat-docked tree keeps its own field, separate
// from the standalone Files tab's `workspace_files_tree_width`.
export function useChatTreeWidthPreference(): {
  chatTreeWidth: number | undefined;
  setChatTreeWidth: (width: number) => void;
  isLoading: boolean;
} {
  const { data, isLoading } = useUserPreferences();
  const mutation = useUpdatePreferences();
  return {
    chatTreeWidth: data?.chat_tree_width,
    setChatTreeWidth: (width) => mutation.mutate({ chat_tree_width: width }),
    isLoading,
  };
}

